import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BdsTooltip } from '@/core/constants/webComponents';
import './FilterSection.scss';

const BdsIcon = 'bds-icon' as React.ElementType;

interface FilterSectionProps {
  title: string;
  description: string;
  badgeCount: number;
  isOpen: boolean;
  onToggle: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}

export const FilterSection: React.FC<FilterSectionProps> = ({
  title,
  description,
  badgeCount,
  isOpen,
  onToggle,
  disabled = false,
  children,
}) => {
  const { t } = useTranslation();
  const [isClosing, setIsClosing] = useState(false);

  const handleToggleClick = () => {
    if (isOpen && !isClosing) {
      setIsClosing(true);
      return;
    }
    if (isClosing) {
      setIsClosing(false);
      return;
    }
    onToggle();
  };

  const handleAnimationEnd = () => {
    if (!isClosing) return;
    setIsClosing(false);
    onToggle();
  };

  const shouldRender = (isOpen || isClosing) && !disabled;
  const contentClass = `filter-section__content${isClosing ? ' filter-section__content--closing' : ''}`;

  return (
    <div className="filter-section">
      <button
        type="button"
        className={`filter-section__header${disabled ? ' filter-section__header--disabled' : ''}`}
        onClick={handleToggleClick}
        disabled={disabled}
      >
        <div className="filter-section__header-row">
          <h3>{title}</h3>
          <div className="filter-section__indicator-block">
            {badgeCount > 0 && (
              <BdsTooltip position="top-right" tooltip-text={t('globalFilter.badgeCountTooltip')}>
                <span className="badge">{badgeCount}</span>
              </BdsTooltip>
            )}
            <span className={`arrow-icon${isOpen && !isClosing ? ' open' : ''}`}>
              <BdsIcon name="arrow-down" />
            </span>
          </div>
        </div>
        {!isOpen && !isClosing && <p className="filter-section__description">{description}</p>}
      </button>

      {shouldRender && (
        <div className={contentClass} onAnimationEnd={handleAnimationEnd}>
          {children}
        </div>
      )}
    </div>
  );
};
